import { Category, CategoryService } from '@core/services/steelfab';
import { ProductService } from '@core/services/steelfab';
import { Product } from '@core/services/steelfab/product.service';
import { TranslateModifiers } from '@core/translate';
import { Component, DefineDependencies } from '@rhtml/component';
import { AngularLayout } from '@rhtml/modifiers';
import { Container } from '@rxdi/core';
import { deepCopy } from '@rxdi/graphql-client';
import { html, LitElement, unsafeCSS } from '@rxdi/lit-html';
import { RouteParams, Router } from '@rxdi/router';
import { SteelFabTheme } from '@shared/styles/theme';
import * as ProductsSListComponentCSS from 'bundle-text:./products-list.component.css';
import { combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * @customElement products-list-component
 */
@Component({
  Settings: {
    selector: 'products-list-component',
    styles: [SteelFabTheme, unsafeCSS(ProductsSListComponentCSS)],
    modifiers: [...AngularLayout, ...TranslateModifiers],
  },
  Providers: DefineDependencies(CategoryService, ProductService)(Container),
  Loading: function (this: ProductsListComponent) {
    return html`
      <div class="products-section">
        <div class="grid">
          ${[1, 2, 3, 4, 5, 6].map(
            () => html`
              <div class="product-card">
                <div class="product-img">
                  <sl-skeleton style="width: 100%; height: 100%; --border-radius: 0;"></sl-skeleton>
                </div>
                <div class="product-body">
                  <sl-skeleton
                    style="width: 70%; height: 20px; margin-bottom: 0.6rem; --border-radius: 4px;"
                  ></sl-skeleton>
                  <sl-skeleton
                    style="width: 100%; height: 14px; margin-bottom: 4px; --border-radius: 4px;"
                  ></sl-skeleton>
                  <sl-skeleton
                    style="width: 85%; height: 14px; margin-bottom: 1rem; --border-radius: 4px;"
                  ></sl-skeleton>

                  <div class="specs-preview">
                    ${[1, 2, 3].map(
                      () => html`
                        <div class="spec-row">
                          <sl-skeleton style="width: 35%; height: 12px; --border-radius: 4px;"></sl-skeleton>
                          <sl-skeleton style="width: 25%; height: 12px; --border-radius: 4px;"></sl-skeleton>
                        </div>
                      `
                    )}
                  </div>

                  <sl-skeleton style="width: 40%; height: 14px; --border-radius: 4px;"></sl-skeleton>
                </div>
              </div>
            `
          )}
        </div>
      </div>
    `;
  },
  State(this: ProductsListComponent, [categoryService, productService]) {
    // The deepest route param is the active category slug
    const categorySlug = this.routeParams?.subtype ?? this.routeParams?.brand ?? this.routeParams?.group;

    return combineLatest([
      productService.getProducts(),
      categoryService.getCategoryTree(),
      categorySlug ? categoryService.getCategoryBySlug(categorySlug) : of(null),
    ]).pipe(
      map(([allProducts, tree, currentCategory]) => {
        // Cache tree on component for use in gotoProduct
        this._categoryTree = tree;

        if (!categorySlug) {
          return { products: allProducts, categories: tree, currentCategory };
        }

        // Find the selected node in the tree and collect all its descendant slugs
        const matchingSlugs = new Set<string>();
        const node = ProductsListComponent.findNodeBySlug(categorySlug, tree);
        if (node) {
          ProductsListComponent.collectDescendantSlugs(node, matchingSlugs);
        } else {
          // Fallback: just match the exact slug
          matchingSlugs.add(categorySlug);
        }

        const products = allProducts.filter((p) => matchingSlugs.has(p.categorySlug));
        return { products, categories: tree, currentCategory };
      })
    );
  },
  Render: ([, productService]) =>
    function (this: ProductsListComponent, state) {
      const { products } = deepCopy(state);
      return html`
        <!-- Products Grid -->
        <div class="products-section">
          <div ngIf=${!products.length} class="empty">
            <div class="empty-icon">🔍</div>
            <p translate>no-result-found</p>
          </div>
          <div ngIf=${products.length} class="grid">
            ${products.map(
              (product) => html`
                <div
                  class="product-card"
                  @mouseover=${() => productService.getProductBySlug(product.slug).subscribe()}
                  @click=${() => this.gotoProduct(product)}
                >
                  <div class="product-img">
                    ${product.images[0]
                      ? html`<img class="product-img" src=${product.images[0]?.url} />`
                      : this.getCategoryIcon(product.categorySlug)}
                  </div>

                  <div class="product-body">
                    <h3 class="product-name">${product.name}</h3>
                    <p class="product-desc">${product.shortDescription}</p>

                    <div class="specs-preview">
                      ${product.specifications.slice(0, 3).map(
                        (s) => html`
                          <div class="spec-row">
                            <span>${s.key}</span>
                            <strong>${s.value}</strong>
                          </div>
                        `
                      )}
                    </div>

                    <span class="btn-link" translate>check-details</span>
                  </div>
                </div>
              `
            )}
            &nbsp;
          </div>
        </div>
      `;
    },
})
export class ProductsListComponent extends LitElement {
  @Router()
  private router: Router;

  @RouteParams()
  private routeParams: { group?: string; brand?: string; subtype?: string };

  private _categoryTree: Category[];

  /**
   * Navigate to product detail, always using 4-segment URL:
   *   /products/:group/:brand/:subtype/:slug
   * When the category hierarchy has fewer than 3 levels, repeat the
   * deepest segment so the URL always has exactly 4 segments.
   */
  private gotoProduct(product: Product) {
    // Build the category path from the tree (most reliable source)
    const tree = this._categoryTree ?? [];
    const catNode = ProductsListComponent.findNodeBySlug(product.categorySlug, tree);

    let segments: string[];

    if (catNode) {
      // Walk up the tree to get all ancestor slugs
      const ancestorPath = ProductsListComponent.buildAncestorPath(catNode, tree);
      // ancestorPath = "/products/group" or "/products/group/brand" or "/products/group/brand/subtype"
      segments = ancestorPath
        .replace(/^\/products\/?/, '')
        .split('/')
        .filter(Boolean);
    } else if (this.routeParams?.group) {
      // Fallback to route params
      segments = [this.routeParams.group];
      if (this.routeParams.brand) segments.push(this.routeParams.brand);
      if (this.routeParams.subtype) segments.push(this.routeParams.subtype);
    } else {
      segments = [product.categorySlug];
    }

    // Ensure exactly 3 category segments by repeating the last one
    while (segments.length < 3) {
      segments.push(segments[segments.length - 1]);
    }

    this.goto(`/products/${segments.join('/')}/${product.slug}`);
  }

  private goto(path: string) {
    this.router.go(path);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  }

  getCategoryIcon(slug: string): string {
    const icons: Record<string, string> = {
      'cnc-frezovi': '⚙️',
      strugove: '🔩',
      shlifovachni: '🛠️',
      probivni: '🔧',
      zavarachno: '🔥',
      specializirano: '🏭',
    };
    return icons[slug] ?? '🏭';
  }

  /**
   * Recursively find a node by slug in a category tree.
   */
  static findNodeBySlug(slug: string, nodes: Category[]): Category | undefined {
    for (const node of nodes) {
      if (node.slug === slug) return node;
      if (node.children?.length) {
        const found = ProductsListComponent.findNodeBySlug(slug, node.children);
        if (found) return found;
      }
    }
    return undefined;
  }

  /**
   * Build the full /products/... path by walking up the parentId chain.
   */
  static buildAncestorPath(node: Category, tree: Category[]): string {
    // Flatten the tree into a map for parentId lookups
    const flatMap = new Map<string, Category>();
    const flatten = (nodes: Category[]) => {
      for (const n of nodes) {
        flatMap.set(n.id, n);
        if (n.children?.length) flatten(n.children);
      }
    };
    flatten(tree);

    const segments: string[] = [];
    let current: Category | undefined = node;
    while (current) {
      segments.unshift(current.slug);
      current = current.parentId ? flatMap.get(current.parentId) : undefined;
    }
    return `/products/${segments.join('/')}`;
  }

  /**
   * Collect the slug of the node itself + all descendant slugs recursively.
   * This allows a parent category to match products in any of its children.
   */
  static collectDescendantSlugs(node: Category, slugs: Set<string>) {
    slugs.add(node.slug);
    if (node.children?.length) {
      for (const child of node.children) {
        ProductsListComponent.collectDescendantSlugs(child, slugs);
      }
    }
  }
}
